/**
 * Branded / phantom types for the Atlas domain.
 *
 * These types encode domain-level semantic distinctions that plain `string`
 * and `number` cannot express. They use TypeScript's intersection-with-tag
 * pattern (branded types) to prevent accidental mixing of incompatible
 * values at compile time while remaining zero-cost at runtime.
 *
 * @example
 * ```ts
 * const hsId = productId<ProductClass.HS92Products>("1234");
 * const sitcId = productId<ProductClass.SITCProducts>("5678");
 * // Type error: cannot pass sitcId where HS92 product ID is expected
 * lookupHS92(sitcId); // TS2345
 * ```
 *
 * @module
 */

import { ProductClass } from "./enums";

// ── Brand utility ───────────────────────────────────────────────────

/**
 * Generic branded-type constructor.
 *
 * `Brand<Base, Tag>` is structurally identical to `Base` at runtime
 * but distinct at the type level. The `__brand` property is declared
 * with `declare` so it never appears in emitted JS.
 */
export type Brand<Base, Tag extends string> = Base & {
  readonly __brand: Tag;
};

// ── Product identity ────────────────────────────────────────────────

/**
 * A product identifier scoped to a specific classification system.
 *
 * Products from different classifications (HS92, HS12, HS22, SITC) occupy
 * disjoint ID spaces. This phantom type parameter prevents accidentally
 * using an HS92 product ID in a SITC lookup or vice versa.
 *
 * @typeParam C - The classification system (e.g. `ProductClass.HS92Products`)
 */
export type ProductId<C extends ProductClass = ProductClass> = Brand<
  string,
  `ProductId:${C}`
>;

/** Construct a classification-scoped product ID. */
export function productId<C extends ProductClass>(
  raw: string,
  _classification?: C,
): ProductId<C> {
  return raw as ProductId<C>;
}

/**
 * Compile-time-only assertion that narrows a string to `ProductId<C>`.
 *
 * Always returns `true` at runtime since branded types are phantom
 * (the brand exists only in the type system). Use this when you need
 * a type guard for control flow narrowing, not for runtime validation.
 *
 * @see {@link productId} to construct a `ProductId` from a known-valid string.
 */
export function isProductId<C extends ProductClass>(
  value: string,
): value is ProductId<C> {
  return typeof value === "string";
}

// ── Location identity ───────────────────────────────────────────────

/**
 * Location ID hierarchy levels.
 *
 * The GraphQL API uses string prefixes (`c123`, `r456`, `s789`) to encode
 * location level. These branded types lift that runtime convention to the
 * type level.
 */
export type CountryLocationId = Brand<string, "CountryLocationId">;
export type RegionLocationId = Brand<string, "RegionLocationId">;
export type SubregionLocationId = Brand<string, "SubregionLocationId">;

/** Any location ID regardless of level. */
export type LocationId =
  | CountryLocationId
  | RegionLocationId
  | SubregionLocationId;

/** Construct a country-level location ID. */
export function countryLocationId(raw: string): CountryLocationId {
  return raw as CountryLocationId;
}

/** Construct a region-level location ID. */
export function regionLocationId(raw: string): RegionLocationId {
  return raw as RegionLocationId;
}

/** Construct a subregion-level location ID. */
export function subregionLocationId(raw: string): SubregionLocationId {
  return raw as SubregionLocationId;
}

// ── Monetary values ─────────────────────────────────────────────────

/**
 * Monetary value in current (nominal) US dollars.
 *
 * Distinct from constant-dollar values to prevent mixing inflation-adjusted
 * and non-adjusted figures in calculations.
 */
export type CurrentUSD = Brand<number, "CurrentUSD">;

/**
 * Monetary value in constant (deflated) US dollars.
 *
 * The base year is determined by the deflator series in use.
 */
export type ConstantUSD = Brand<number, "ConstantUSD">;

/**
 * Per-capita monetary value.
 *
 * @typeParam Base - The underlying monetary type (`CurrentUSD` or `ConstantUSD`)
 */
export type PerCapita<Base extends CurrentUSD | ConstantUSD = CurrentUSD> =
  Brand<number, `PerCapita:${Base["__brand"]}`>;

/** Construct a current-USD value. */
export function currentUSD(value: number): CurrentUSD {
  return value as CurrentUSD;
}

/** Construct a constant-USD value. */
export function constantUSD(value: number): ConstantUSD {
  return value as ConstantUSD;
}

/** Construct a per-capita value. */
export function perCapita<Base extends CurrentUSD | ConstantUSD>(
  value: number,
): PerCapita<Base> {
  return value as PerCapita<Base>;
}

// ── Complexity metrics ──────────────────────────────────────────────

/**
 * Economic Complexity Index — a z-score (unbounded, centered near 0).
 */
export type ECI = Brand<number, "ECI">;

/**
 * Product Complexity Index — a z-score (unbounded, centered near 0).
 */
export type PCI = Brand<number, "PCI">;

/**
 * Revealed Comparative Advantage — a ratio (≥ 0, threshold at 1).
 */
export type RCA = Brand<number, "RCA">;

/**
 * Complexity Outlook Index — measures potential for growth
 * based on proximity to complex products.
 */
export type COI = Brand<number, "COI">;

/**
 * Complexity Outlook Gain — marginal gain from adding a product.
 */
export type COG = Brand<number, "COG">;

/**
 * Distance — measures how far a country is from having RCA in a product.
 * Range: [0, 1], lower means closer.
 */
export type Distance = Brand<number, "Distance">;

/**
 * Compound Annual Growth Rate — a growth rate, can be negative.
 */
export type CAGR = Brand<number, "CAGR">;

/** Construct an ECI value. */
export function eci(value: number): ECI {
  return value as ECI;
}

/** Construct a PCI value. */
export function pci(value: number): PCI {
  return value as PCI;
}

/** Construct an RCA value. */
export function rca(value: number): RCA {
  return value as RCA;
}

/** Construct a COI value. */
export function coi(value: number): COI {
  return value as COI;
}

/** Construct a COG value. */
export function cog(value: number): COG {
  return value as COG;
}

/** Construct a Distance value. */
export function distance(value: number): Distance {
  return value as Distance;
}

/** Construct a CAGR value. */
export function cagr(value: number): CAGR {
  return value as CAGR;
}

// ── Year range ──────────────────────────────────────────────────────

/**
 * A validated year range where `start <= end`.
 *
 * Constructed via `yearRange()` which enforces the ordering invariant.
 * Prefer this over separate `startYear` / `endYear` fields to make
 * invalid ranges (start > end) unrepresentable.
 */
export interface ValidatedYearRange {
  readonly start: number;
  readonly end: number;
  readonly __brand: "ValidatedYearRange";
}

/**
 * Construct a validated year range.
 *
 * @throws {RangeError} if `start > end`
 */
export function yearRange(start: number, end: number): ValidatedYearRange {
  if (start > end) {
    throw new RangeError(
      `Invalid year range: start (${start}) must be <= end (${end})`,
    );
  }
  return { start, end } as ValidatedYearRange;
}

/**
 * Safely attempt to create a year range, returning `null` if invalid.
 */
export function tryValidatedYearRange(
  start: number | null | undefined,
  end: number | null | undefined,
): ValidatedYearRange | null {
  if (start == null || end == null || start > end) return null;
  return { start, end } as ValidatedYearRange;
}

/** Duration of a year range in years (inclusive). */
export function yearRangeDuration(range: ValidatedYearRange): number {
  return range.end - range.start + 1;
}

// ── Type narrowing utilities ────────────────────────────────────────

/**
 * Extract the raw base type from a branded type.
 *
 * Useful at serialization boundaries where branded types
 * need to be converted back to plain primitives.
 *
 * @example
 * ```ts
 * type Raw = Unbrand<CurrentUSD>; // number
 * type RawId = Unbrand<ProductId<ProductClass.HS92Products>>; // string
 * ```
 */
export type Unbrand<T> = T extends Brand<infer Base, string> ? Base : T;
